import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Firm-settings surface (T-0110 / E10 wave 6).
 *
 * Two API surfaces are joined here:
 *
 *   - **Users & roles** — T-0020 `GET /api/v1/users` + per-user provision /
 *     deactivate / permission-override paths. The settings screen renders
 *     the read-side; mutations (invite, deactivate, override) are exposed
 *     as hooks so future settings/users sub-screens can compose them
 *     without re-deriving the shapes.
 *
 *   - **Client viewers** — T-0026 + the T-0110 list endpoint
 *     `GET /api/v1/client-viewers`. The schema returns the joined Client +
 *     Project name so the table can render scope strings without a
 *     follow-up fetch.
 *
 * Both surfaces are firm_admin / engagement_lead gated at the API layer
 * (consultants get 403); the UI relies on that and does not duplicate the
 * authorization checks client-side.
 */

// ===========================================================================
// Users (T-0020 / E01-F07)
// ===========================================================================

/**
 * Read shape returned by `GET /api/v1/users`. The backend service
 * (`apps/api/src/services/users.ts → listUsers`) returns a thin row per user.
 *
 * T-0127 (PR #143) added `last_sign_in_at` (and a `two_factor_enabled`
 * column) to the User schema. The table renders last sign-in; it no longer
 * reads `two_factor_enabled` (T-0227 / C2-20): MFA is Entra policy and the
 * API cannot read enrollment from Entra, so the flag said nothing true. The
 * schema passes the field through untouched for any other reader.
 */
export const FirmUserListItemSchema = z
  .object({
    id: z.string().uuid(),
    email: z.string(),
    name: z.string(),
    default_role: z.string(),
    status: z.string(),
    last_sign_in_at: z.string().nullable().optional(),
  })
  .passthrough();
export type FirmUserListItem = z.infer<typeof FirmUserListItemSchema>;

export const FirmUserListSchema = z.array(FirmUserListItemSchema);
export type FirmUserList = z.infer<typeof FirmUserListSchema>;

export function listFirmUsers(): Promise<FirmUserList> {
  return apiClient('/api/v1/users', FirmUserListSchema);
}

export function useFirmUsersQuery() {
  return useQuery({
    queryKey: ['settings', 'users'] as const,
    queryFn: () => listFirmUsers(),
    staleTime: 30_000,
  });
}

/**
 * POST /api/v1/users — provision a new internal user. Wired to the
 * "Invite user" affordance on the users settings screen. Returns
 * `{ id, status }`; the screen invalidates the list query so the new
 * row appears.
 */
export const ProvisionUserResultSchema = z.object({
  id: z.string().uuid(),
  status: z.string(),
});
export type ProvisionUserResult = z.infer<typeof ProvisionUserResultSchema>;

export interface ProvisionUserInput {
  email: string;
  name: string;
  default_role: 'firm_admin' | 'engagement_lead' | 'consultant';
}

export function provisionUser(input: ProvisionUserInput): Promise<ProvisionUserResult> {
  return apiClient('/api/v1/users', ProvisionUserResultSchema, {
    method: 'POST',
    body: input,
  });
}

export function useProvisionUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: provisionUser,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'users'] });
    },
  });
}

// ===========================================================================
// Client viewers (T-0026 + T-0110)
// ===========================================================================

export const ClientViewerGrantSchema = z.object({
  grant_id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  client_id: z.string().uuid(),
  client_name: z.string(),
  project_id: z.string().uuid().nullable(),
  project_name: z.string().nullable(),
  viewer_email: z.string(),
  viewer_name: z.string(),
  auth_mode: z.string(),
  status: z.string(),
  issued_by: z.string().uuid(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ClientViewerGrant = z.infer<typeof ClientViewerGrantSchema>;

export const ClientViewerGrantListSchema = z.object({
  items: z.array(ClientViewerGrantSchema),
});
export type ClientViewerGrantList = z.infer<typeof ClientViewerGrantListSchema>;

export function listClientViewerGrants(): Promise<ClientViewerGrantList> {
  return apiClient('/api/v1/client-viewers', ClientViewerGrantListSchema);
}

export function useClientViewerGrantsQuery() {
  return useQuery({
    queryKey: ['settings', 'viewers'] as const,
    queryFn: () => listClientViewerGrants(),
    staleTime: 30_000,
  });
}

/**
 * POST /api/v1/client-viewers — invite a new viewer (the "Invite viewer"
 * affordance + the "Issue magic link" CTA when re-inviting an existing
 * viewer email). Returns the grant id + a one-time invite URL when
 * `auth_mode='magic_link'`.
 */
export const InviteViewerResultSchema = z
  .object({
    grant_id: z.string().uuid(),
    status: z.string(),
    auth_mode: z.string(),
    invite_url: z.string().optional(),
  })
  .passthrough();
export type InviteViewerResult = z.infer<typeof InviteViewerResultSchema>;

export interface InviteViewerInput {
  viewer_email: string;
  viewer_name: string;
  client_id: string;
  project_id?: string | null;
  auth_mode: 'magic_link' | 'sso';
}

export function inviteClientViewer(input: InviteViewerInput): Promise<InviteViewerResult> {
  return apiClient('/api/v1/client-viewers', InviteViewerResultSchema, {
    method: 'POST',
    body: input,
  });
}

export function useInviteViewerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inviteClientViewer,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'viewers'] });
    },
  });
}

/**
 * POST /api/v1/client-viewers/:id/reissue — wired to the "Resend" affordance
 * on pending invitations + the "Re-issue magic link" action in the viewer
 * preview modal.
 */
export const ReissueViewerResultSchema = z.object({
  grant_id: z.string().uuid(),
  status: z.string(),
});
export type ReissueViewerResult = z.infer<typeof ReissueViewerResultSchema>;

export function reissueClientViewer(grantId: string): Promise<ReissueViewerResult> {
  return apiClient(
    `/api/v1/client-viewers/${encodeURIComponent(grantId)}/reissue`,
    ReissueViewerResultSchema,
    { method: 'POST' },
  );
}

export function useReissueViewerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: reissueClientViewer,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'viewers'] });
    },
  });
}

/**
 * POST /api/v1/client-viewers/:id/revoke — wired to the "Revoke" affordance
 * on the viewers table.
 */
export const RevokeViewerResultSchema = z.object({
  grant_id: z.string().uuid(),
  status: z.literal('revoked'),
});
export type RevokeViewerResult = z.infer<typeof RevokeViewerResultSchema>;

export function revokeClientViewer(grantId: string): Promise<RevokeViewerResult> {
  return apiClient(
    `/api/v1/client-viewers/${encodeURIComponent(grantId)}/revoke`,
    RevokeViewerResultSchema,
    { method: 'POST' },
  );
}

export function useRevokeViewerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: revokeClientViewer,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'viewers'] });
    },
  });
}
